import React from 'react';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faDownload,
  faPlus,
  faSpinner,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';

import type {ILongTermCropsSubStepProps} from '../productionInfo.types';

const faPlusIcon = faPlus as unknown as IconProp;
const faDownloadIcon = faDownload as unknown as IconProp;
const faUploadIcon = faUpload as unknown as IconProp;
const faSpinnerIcon = faSpinner as unknown as IconProp;
const faCopyIcon = faCopy as unknown as IconProp;
const faTrashIcon = faTrash as unknown as IconProp;

export const LongTermCropsSubStep: React.FC<ILongTermCropsSubStepProps> = ({
  longTermRows,
  longTermColumns,
  longTermCropOptions,
  longTermVarietyOptionsByCropId,
  longTermTableErrors,
  longTermTableErrorMessage,
  isLoadingLongTermCrops,
  isSavingLongTermCrops,
  longTermUploadInputRef,
  onAddLongTermRow,
  onDuplicateLongTermRow,
  onRemoveLongTermRow,
  onUpdateLongTermRowField,
  onDownloadLongTermTemplate,
  onOpenLongTermTemplatePicker,
  onLongTermTemplateUpload,
}) => {
  return (
    <div className="application-card long-term-crops-inline">
      <div className="long-term-crops-inline__header">
        <h3>Long-term crops</h3>
        <p>
          Capture orchard/tree census data in one table. Each row represents one
          block.
        </p>
      </div>

      <div className="long-term-crops-inline__toolbar">
        <div className="long-term-crops-inline__toolbar-actions">
          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onAddLongTermRow}
            disabled={isLoadingLongTermCrops || isSavingLongTermCrops}
          >
            <FontAwesomeIcon icon={faPlusIcon} />
            <span>Add block</span>
          </button>
          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onDownloadLongTermTemplate}
            disabled={isLoadingLongTermCrops || isSavingLongTermCrops}
          >
            <FontAwesomeIcon icon={faDownloadIcon} />
            <span>Download template</span>
          </button>
          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onOpenLongTermTemplatePicker}
            disabled={isLoadingLongTermCrops || isSavingLongTermCrops}
          >
            <FontAwesomeIcon icon={faUploadIcon} />
            <span>Upload template</span>
          </button>
          <input
            ref={longTermUploadInputRef}
            className="sr-only"
            type="file"
            accept={[
              '.xlsx',
              '.xls',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
            ].join(',')}
            onChange={(event) => {
              onLongTermTemplateUpload(event);
            }}
          />
        </div>
        <p>
          {longTermRows.length} block{longTermRows.length === 1 ? '' : 's'}{' '}
          prepared
        </p>
      </div>

      <div className="long-term-crops-inline__body" aria-live="polite">
        {isLoadingLongTermCrops ? (
          <div className="long-term-crops-inline__loading" role="status">
            <FontAwesomeIcon icon={faSpinnerIcon} spin />
            <p>Loading long-term crops...</p>
          </div>
        ) : (
          <>
            {longTermTableErrorMessage ? (
              <div className="form-alert is-error" role="alert">
                {longTermTableErrorMessage}
              </div>
            ) : null}

            {longTermRows.length > 0 ? (
              <div className="long-term-crops-inline__table-wrap">
                <table className="long-term-crops-table">
                  <thead>
                    <tr>
                      <th className="is-col-row-actions" scope="col">
                        Actions
                      </th>
                      {longTermColumns.map((column) => (
                        <th
                          key={column.key}
                          className={column.className}
                          scope="col"
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {longTermRows.map((row, rowIndex) => {
                      const rowErrors = longTermTableErrors[row.rowId] || {};
                      const hasRowErrors = Object.keys(rowErrors).length > 0;
                      const blockLabel =
                        row.blockNumber.trim() || `row ${rowIndex + 1}`;

                      return (
                        <tr
                          key={row.rowId}
                          className={hasRowErrors ? 'is-row-error' : undefined}
                        >
                          <td className="long-term-crops-table__actions is-col-row-actions">
                            <div className="long-term-crops-table__actions-inner">
                              <div className="long-term-crops-table__row-actions">
                                <button
                                  type="button"
                                  className="long-term-crops-table__icon-btn is-secondary"
                                  onClick={() =>
                                    onDuplicateLongTermRow(row.rowId)
                                  }
                                  disabled={isSavingLongTermCrops}
                                  aria-label={`Duplicate ${blockLabel}`}
                                  title="Duplicate row"
                                >
                                  <FontAwesomeIcon icon={faCopyIcon} />
                                </button>
                                <button
                                  type="button"
                                  className="long-term-crops-table__icon-btn is-danger"
                                  onClick={() => onRemoveLongTermRow(row.rowId)}
                                  disabled={isSavingLongTermCrops}
                                  aria-label={`Remove ${blockLabel}`}
                                  title="Delete row"
                                >
                                  <FontAwesomeIcon icon={faTrashIcon} />
                                </button>
                              </div>
                              {rowErrors.general ? (
                                <span className="long-term-crops-table__error">
                                  {rowErrors.general}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          {longTermColumns.map((column) => {
                            const controlId = `long-term-${column.key}-${row.rowId}`;
                            const value = String(
                              (
                                row as unknown as Record<
                                  string,
                                  string | number | undefined
                                >
                              )[column.key] ?? '',
                            );
                            const errorMessage = rowErrors[column.key];

                            return (
                              <td
                                key={`${row.rowId}-${column.key}`}
                                className={column.className}
                              >
                                <div className="long-term-crops-table__field">
                                  <label
                                    className="sr-only"
                                    htmlFor={controlId}
                                  >
                                    {column.label} for {blockLabel}
                                  </label>

                                  {column.type === 'select' ? (
                                    <select
                                      id={controlId}
                                      className="long-term-crops-table__control"
                                      value={value}
                                      onChange={(event) =>
                                        onUpdateLongTermRowField(
                                          row.rowId,
                                          column.key,
                                          event.target.value,
                                        )
                                      }
                                      disabled={isSavingLongTermCrops}
                                    >
                                      {column.placeholder !== undefined ? (
                                        <option value="">
                                          {column.placeholder}
                                        </option>
                                      ) : null}
                                      {(() => {
                                        const selectOptions = (
                                          column.key === 'cropId'
                                            ? longTermCropOptions.map(
                                                (crop) => ({
                                                  value: crop.id,
                                                  label: crop.name,
                                                }),
                                              )
                                            : column.key === 'type'
                                            ? longTermVarietyOptionsByCropId.get(
                                                row.cropId,
                                              ) || []
                                            : column.options || []
                                        ).slice();

                                        const hasCurrentValue =
                                          selectOptions.some(
                                            (option) => option.value === value,
                                          );
                                        if (
                                          column.key === 'type' &&
                                          value.trim() &&
                                          !hasCurrentValue
                                        ) {
                                          selectOptions.push({
                                            value,
                                            label: value,
                                          });
                                        }

                                        return selectOptions;
                                      })().map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : column.type === 'textarea' ? (
                                    <textarea
                                      id={controlId}
                                      className="long-term-crops-table__control long-term-crops-table__control--notes"
                                      value={value}
                                      onChange={(event) =>
                                        onUpdateLongTermRowField(
                                          row.rowId,
                                          column.key,
                                          event.target.value,
                                        )
                                      }
                                      disabled={isSavingLongTermCrops}
                                    />
                                  ) : column.key === 'spacing' ? (
                                    <div className="long-term-crops-table__input-suffix">
                                      <input
                                        id={controlId}
                                        className={
                                          'long-term-crops-table__control long-term-crops-table__control--with-suffix'
                                        }
                                        type={column.type}
                                        min={column.min}
                                        step={column.step}
                                        inputMode="numeric"
                                        value={value}
                                        onChange={(event) =>
                                          onUpdateLongTermRowField(
                                            row.rowId,
                                            column.key,
                                            event.target.value,
                                          )
                                        }
                                        disabled={isSavingLongTermCrops}
                                      />
                                      <span className="long-term-crops-table__suffix">
                                        m
                                      </span>
                                    </div>
                                  ) : (
                                    <input
                                      id={controlId}
                                      className="long-term-crops-table__control"
                                      type={column.type}
                                      min={column.min}
                                      step={column.step}
                                      inputMode={
                                        column.type === 'number'
                                          ? 'decimal'
                                          : undefined
                                      }
                                      value={value}
                                      onChange={(event) =>
                                        onUpdateLongTermRowField(
                                          row.rowId,
                                          column.key,
                                          event.target.value,
                                        )
                                      }
                                      disabled={isSavingLongTermCrops}
                                    />
                                  )}

                                  {errorMessage ? (
                                    <span className="long-term-crops-table__error">
                                      {errorMessage}
                                    </span>
                                  ) : null}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="long-term-crops-inline__empty">
                <h3>No blocks added yet</h3>
                <p>Add your first long-term crop block to continue.</p>
                <button
                  type="button"
                  className="money-btn money-btn--secondary money-btn--sm"
                  onClick={onAddLongTermRow}
                  disabled={isSavingLongTermCrops}
                >
                  <FontAwesomeIcon icon={faPlusIcon} />
                  <span>Add first block</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
